export interface SearchRequest {
	query: string;
	filters: string;
	attributesToRetrieve: string[];
	attributesToHighlight: string[];
	attributesToSnippet: string[];
	distinct: boolean | number;
	enableRules?: boolean;
	getRankingInfo?: boolean;
	hitsPerPage: number;
	page: number;
	facets: string[];
	ruleContexts?: string[];
}

export type Facets = Record<string, Record<string, number>>;

export type FacetsStats = Record<string, {
	min: number;
	max: number;
	avg: number;
	sum: number;
}>;

export interface SearchResponse<T extends Record<string, unknown> = { objectID: string }> {
	hits: T[];
	nbHits: number;
	page: number;
	nbPages: number;
	hitsPerPage: number;
	facets: Facets;
	facets_stats?: FacetsStats;
	exhaustiveFacetsCount: boolean;
	exhaustiveNbHits: boolean;
	exhaustiveTypo: boolean;
	exhaustive: {
		facetsCount: boolean;
		nbHits: boolean;
		typo: boolean;
	};
	query: string;
	params: string;
	renderingContent: Record<string, unknown>;
	processingTimeMS: number;
	appliedRules?: Array<Record<string, unknown>>;
	processingTimingsMS?: {
		_request?: { roundTrip: number };
		extensions?: number;
		getIdx?: { load?: { total: number }; total?: number };
		total?: number;
		[k: string]: unknown;
	};
	serverTimeMS?: number;
}


export class AlgoliaClient {
	private collectionHandleCache = new Map<string, string>();

	constructor(private apiKey: string, private appId: string, private index: string) {}

	setIndex(newIndex: string): void {
		this.index = newIndex;
	}

	getIndex(): string {
		return this.index;
	}

	private toCollectionIdFilter(collectionId: string | number): string | null {
		const numericId = Number(collectionId);
		if (Number.isFinite(numericId)) {
			return `collection_ids:${numericId}`;
		}
		return null;
	}

	private async executeSearch<T extends Record<string, unknown>>(
		request: SearchRequest
	): Promise<SearchResponse<T>> {
		console.log(`[Algolia] → ${this.index}`, {
			filters: request.filters,
			ruleContexts: request.ruleContexts ?? "(none)",
			enableRules: request.enableRules,
			page: request.page,
			hitsPerPage: request.hitsPerPage
		});

		const response = await fetch(`https://${this.appId}.algolia.net/1/indexes/${this.index}/query`, {
			method: "POST",
			headers: {
				"content-type": "application/json",
				"x-algolia-api-key": this.apiKey,
				"x-algolia-application-id": this.appId
			},
			body: JSON.stringify(request)
		});

		const responseJson = await response.json() as SearchResponse<T>;
		const appliedRulesRaw = (responseJson as unknown as Record<string, unknown>).appliedRules;
		console.log(`[Algolia] ← ${this.index}`, {
			nbHits: responseJson.nbHits,
			appliedRules: Array.isArray(appliedRulesRaw) && appliedRulesRaw.length > 0
				? (appliedRulesRaw as Record<string, unknown>[]).map((r) => r.objectID ?? r)
				: appliedRulesRaw ?? "(none — campo assente nella risposta)",
			renderingContent: responseJson.renderingContent,
		});
		return responseJson;
	}

	async discoverHandleFromId(collectionId: string | number): Promise<string | null> {
		const numericId = Number(collectionId);
		if (!Number.isFinite(numericId)) return null;

		const cacheKey = String(numericId);
		const cached = this.collectionHandleCache.get(cacheKey);
		if (cached) return cached;

		const request: SearchRequest = {
			query: "",
			filters: `collection_ids:${numericId}`,
			attributesToRetrieve: ["collection_ids", "collections"],
			distinct: true,
			attributesToHighlight: [],
			attributesToSnippet: [],
			hitsPerPage: 1,
			page: 0,
			facets: []
		};

		const response = await this.executeSearch<Record<string, unknown>>(request);
		const hit = response?.hits?.[0] as { collection_ids?: number[]; collections?: string[] } | undefined;
		if (!hit || !Array.isArray(hit.collection_ids) || !Array.isArray(hit.collections)) return null;

		const idx = hit.collection_ids.indexOf(numericId);
		if (idx === -1) return null;

		const handle = hit.collections[idx];
		if (typeof handle !== "string" || handle.length === 0) return null;

		this.collectionHandleCache.set(cacheKey, handle);
		return handle;
	}

	async search<T extends Record<string, unknown>>(
		collectionHandle: string,
		size: number,
		page: number,
		filters: string[] = [],
		collectionId?: string | number,
		ruleContexts?: string[],
		opts?: {
			/**
			 * Se true, usa un filtro combinato (collection_ids:id OR collections:"handle")
			 * in una singola query, senza tentare la discovery waterfall su Algolia.
			 * Copre sia indici con collection_ids sia indici con solo collections:"handle".
			 * Riduce da 3 a 1 query per chiamata.
			 */
			skipHandleDiscovery?: boolean;
		}
	): Promise<SearchResponse<T>> {
		const collectionIdFilter = collectionId != null ? this.toCollectionIdFilter(collectionId) : null;
		const baseRuleContexts = ruleContexts && ruleContexts.length > 0 ? [...ruleContexts] : undefined;

		const buildRequest = (filtersToApply: string[], contexts?: string[]): SearchRequest => {
			const request: SearchRequest = {
				query: "",
				filters: filtersToApply.join(" AND "),
				attributesToRetrieve: ["handle"],
				distinct: true,
				enableRules: true,
				getRankingInfo: true,
				attributesToHighlight: [],
				attributesToSnippet: [],
				hitsPerPage: size,
				page: page,
				facets: ["*"]
			};
			if (contexts && contexts.length > 0) {
				request.ruleContexts = contexts;
			}
			return request;
		};

		// --- Fast path ---
		if (opts?.skipHandleDiscovery && (collectionIdFilter || collectionHandle)) {
			const collectionFilter = collectionHandle
				? `collections:"${collectionHandle.replace(/"/g, '\\"')}"`
				: collectionIdFilter!;
			console.log("[Algolia] fast path filter:", collectionFilter);
			const firstResponse = await this.executeSearch<T>(buildRequest([collectionFilter, ...filters], baseRuleContexts));

			if (firstResponse.nbHits === 0 && collectionHandle && collectionIdFilter) {
				console.log("[Algolia] fast path: 0 hits con handle filter, retry con collection_ids");
				return this.executeSearch<T>(buildRequest([collectionIdFilter, ...filters], baseRuleContexts));
			}

			return firstResponse;
		}

		// --- Path legacy: handle-based con discovery fallback (compatibilità) ---
		const filterToApply: string[] = [];
		const preferHandleFilter = !!(ruleContexts && ruleContexts.length > 0 && collectionHandle);

		if (preferHandleFilter) {
			const safeHandle = collectionHandle.replace(/"/g, '\\"');
			filterToApply.push(`collections:"${safeHandle}"`);
		} else if (collectionIdFilter) {
			filterToApply.push(collectionIdFilter);
		} else if (collectionHandle) {
			const safeHandle = collectionHandle.replace(/"/g, '\\"');
			filterToApply.push(`collections:"${safeHandle}"`);
		}

		filterToApply.push(...filters);

		let responseJson = await this.executeSearch<T>(buildRequest(filterToApply, baseRuleContexts));

		if (responseJson.hits.length === 0 && collectionIdFilter && preferHandleFilter) {
			const discoveredHandle = await this.discoverHandleFromId(collectionId as string | number);
			if (discoveredHandle && discoveredHandle !== collectionHandle) {
				const safeHandle = discoveredHandle.replace(/"/g, '\\"');
				const handleFilters = [`collections:"${safeHandle}"`, ...filters];
			let nextContexts = baseRuleContexts;
				if (baseRuleContexts && baseRuleContexts.length === 1 && baseRuleContexts[0] === collectionHandle) {
					nextContexts = [discoveredHandle];
				}
				responseJson = await this.executeSearch<T>(buildRequest(handleFilters, nextContexts));
			} else {
				const idFilters = [collectionIdFilter, ...filters];
				responseJson = await this.executeSearch<T>(buildRequest(idFilters, baseRuleContexts));
			}
		}

		return responseJson;
	}

	/**
	 * Ricerca testuale senza filtro collection — usata dalla pagina search results.
	 * A differenza di search(), la query è il testo inserito dall'utente e non viene
	 * aggiunto nessun filtro di collection.
	 */
	async rawSearch<T extends Record<string, unknown>>(
		query: string,
		size: number,
		page: number,
		filters: string[] = [],
		ruleContexts?: string[]
	): Promise<SearchResponse<T>> {
		const request: SearchRequest = {
			query,
			filters: filters.join(" AND "),
			attributesToRetrieve: ["handle"],
			distinct: true,
			enableRules: true,
			getRankingInfo: true,
			attributesToHighlight: [],
			attributesToSnippet: [],
			hitsPerPage: size,
			page,
			facets: ["*"]
		};
		if (ruleContexts && ruleContexts.length > 0) {
			request.ruleContexts = ruleContexts;
		}
		return this.executeSearch<T>(request);
	}
}
